import React, { Dispatch, useEffect, useImperativeHandle, useMemo, useState } from "react";
import { Card, Accordion, Row, Col, InputGroup } from "react-bootstrap";
import { useSearchParams } from "react-router-dom";
import { useFormContext, useWatch, get } from "react-hook-form";

import { ReactComponent as SortIcon } from "assets/images/icons/SortDown.svg";
import { getServiceDetailsConfig } from "api";
import { RequestFormAction, RequestFormActionType, RequestFormState } from "pages/shipper/RequestForm/RequestFormState";
import { FormattedMessage, useIntl } from "react-intl";
import Textarea from "components/atoms/Textarea/Textarea";
import { filterWhenNonSelected, getObjectByKeys } from "helpers/commonHelpers";
import {
  getSpecialRequirementValidationMessage,
  isValidSpecialRequirementAdditionalInformation,
} from "helpers/validationHelpers";
import { ReactComponent as CheckIcon } from "assets/images/icons/Check.svg";
import { ReactComponent as ExclamationIcon } from "assets/images/icons/Exclamation.svg";
import Alert from "components/molecules/Alert/Alert";
import { getIncotermTranslations, getServiceTranslations } from "helpers/translationHelpers";
import { resolveDefaultIncoterms } from "@RequestFormV2/utils/resolveDefaultIncoterms";
import usePermissions from "hooks/usePermissions";
import { FormStepTypes, FormStepValidationTypes } from "constants/";
import testIds from "constants/testIds";
import FormHeader from "components/v2/FormSection/components/FormHeader/FormHeader";
import FormSection from "components/v2/FormSection/FormSection";
import { getHasMultipleCountriesForQuoteRequest } from "pages/shipper/RequestForm/utils/getHasMultipleCountriesForQuoteRequest";
import FormItemInfo from "../Common/FormItemInfo";
import { EXPORT_CLEARANCE_OPTIONS, IMPORT_CLEARANCE_OPTIONS, TRANSIT_CLEARANCE_OPTIONS } from "./ServiceFormDefaultOptions";
import ServiceRequirementForm from "./ServiceRequirementForm";
import SearchSelect from "../Common/SearchSelect";
import FormHr from "../Common/FormHr";
import "./serviceForm.scss";
import ProviderAttachmentForm from "../Provider/ProviderAttachmentForm";

type ServiceFormProps = {
  state: RequestFormState;
  dispatch: Dispatch<RequestFormAction>;
  isQuoteRequest: boolean;
  validateSection?: Function;
  shouldValidateSection?: React.MutableRefObject<boolean>;
  isConsolidated?: boolean;
  handleDraftSave?: () => Promise<void>;
  childRef?: React.MutableRefObject<any>;
};

function ServiceForm(props: ServiceFormProps) {
  const {
    state,
    dispatch,
    isQuoteRequest,
    validateSection,
    shouldValidateSection,
    isConsolidated,
    handleDraftSave,
    childRef,
  } = props;
  const intl = useIntl();
  const { getPaymentLocationLabel } = usePermissions();
  const [isFetching, setIsFetching] = useState(false);
  const [bucketAttachments, setBucketAttachments] = useState([]);
  const {
    getValues,
    setValue,
    register,
    setError,
    unregister,
    formState: { errors },
  } = useFormContext();
  const [insuranceEnabled, transitClearance, importClearance, exportClearance] = useWatch({
    name: ["service.insurance", "service.transit_clearance", "service.import_clearance", "service.export_clearance"],
  });
  const [pickupCountry, stop1Country, pickupPortCountry, stop1PortCountry, scheduleStops] = useWatch({
    name: [
      "schedule.pickup_country",
      "schedule.stop1_country",
      "schedule.pickup_port_country",
      "schedule.stop1_port_country",
      "schedule.stops",
    ],
  });
  const resolvedPickupCountry = pickupCountry || pickupPortCountry;
  const resolvedDeliveryCountry = stop1Country || stop1PortCountry;
  // Not memoized: stop countries are filled in after a draft loads via nested setValue,
  // which mutates `schedule.stops` in place and keeps the same array reference.
  const hasMultipleCountries = getHasMultipleCountriesForQuoteRequest({
    stops: scheduleStops,
    pickupCountry,
    deliveryCountry: stop1Country,
    pickupPortCountry,
    deliveryPortCountry: stop1PortCountry,
  });
  const [searchParams] = useSearchParams();
  const currentActiveStep = searchParams.get("active_step");
  const serviceFields = FormStepValidationTypes[FormStepTypes.SERVICE] as string[];
  if (!isQuoteRequest && !serviceFields.includes("bucket_attachments")) {
    serviceFields.push("bucket_attachments");
  }
  if (state.service?.request_service_requirement) {
    const requirementValues = state.service?.request_service_requirement_values ?? {};
    Object.values(requirementValues).forEach((item: { value: string }) => {
      if (getValues(`service.special_requirements.${item.value}.value`) === "true") {
        const fieldPath = `service.special_requirements.${item.value}.additional_information`;
        if (!serviceFields.includes(fieldPath)) {
          serviceFields.push(fieldPath);
        }
      }
    });
  }
  const hasError = serviceFields.some((item) => get(errors, item));

  useImperativeHandle(childRef, () => ({ checkStepValidation }));

  const showClearance = useMemo(
    () => (!isQuoteRequest && state.service?.ask_clearance) || (isQuoteRequest && hasMultipleCountries),
    [hasMultipleCountries, isQuoteRequest, state.service],
  );

  const activeAndRequiredBucketAttachments: any = useMemo(
    () => (bucketAttachments || []).filter((item: any) => item.is_active && item.is_mandatory),
    [bucketAttachments],
  );

  const bucketAttachmentHasMap: any = useMemo(
    () => getObjectByKeys(getValues("bucket_attachments") || []),
    [getValues("bucket_attachments")],
  );

  useEffect(() => {
    if (!getValues("organizational.location")) return;
    setIsFetching(true);
    getServiceDetailsConfig(getValues("organizational.location"))
      .then((res) => {
        const updatedData = { ...res.data?.service_options };
        if (updatedData.request_service_requirement_values) {
          // TODO: remove use of any
          updatedData.request_service_requirement_values = getObjectByKeys(
            updatedData.request_service_requirement_values,
            "value",
          ) as any;
        }
        if (updatedData.incoterm_values) {
          // TODO: remove use of any
          updatedData.incoterm_values = Object.values(getObjectByKeys(updatedData.incoterm_values, "value") as any);
          updatedData.incoterm_values = updatedData.incoterm_values.map((item) => ({
            label: getIncotermTranslations(item.label, intl),
            value: item.value,
          }));
        }
        if (updatedData.service_type_options) {
          updatedData.service_type_options = updatedData.service_type_options.map((item) => ({
            label: getServiceTranslations(item.label, intl),
            value: item.value,
          }));
        }
        dispatch({ type: RequestFormActionType.SERVICE_REQUEST_SUCCESS, payload: updatedData });
        applyDefaultIncoterms(updatedData.incoterm, updatedData.incoterm_default);
        if (res.data?.service_options?.request_service_requirement) {
          setDefaultSpecialRequirements(res.data.service_options.request_service_requirement_values ?? []);
        } else {
          clearSpecialRequirements();
        }
        if (shouldValidateSection && validateSection && shouldValidateSection.current) {
          setTimeout(() => {
            validateSection(FormStepTypes.SERVICE);
          }, 200);
        }
      })
      .catch(() => {})
      .finally(() => {
        setIsFetching(false);
      });
  }, [getValues("organizational.location")]);

  const checkStepValidation = () => {
    if (
      activeAndRequiredBucketAttachments.length &&
      (!getValues("bucket_attachments")?.length ||
        !activeAndRequiredBucketAttachments.every((item: any) => bucketAttachmentHasMap[item.id]))
    ) {
      setError("bucket_attachments", {
        type: "custom",
      });
      return false;
    }

    if (state.service?.request_service_requirement) {
      const requirementValues = state.service?.request_service_requirement_values ?? {};
      let hasInvalidRequirement = false;

      Object.values(requirementValues).forEach(
        (item: { value: string; data_type?: string; additional_information_required?: boolean }) => {
        if (getValues(`service.special_requirements.${item.value}.value`) !== "true") return;

        const additionalInformation = getValues(`service.special_requirements.${item.value}.additional_information`);
        const dataType = item.data_type ?? "string";
        const isAdditionalInfoRequired = item.additional_information_required ?? true;

        if (
          !isValidSpecialRequirementAdditionalInformation(
            additionalInformation,
            dataType,
            isAdditionalInfoRequired,
          )
        ) {
          setError(`service.special_requirements.${item.value}.additional_information`, {
            type: "custom",
            message: getSpecialRequirementValidationMessage(dataType),
          });
          hasInvalidRequirement = true;
        }
      });

      if (hasInvalidRequirement) return false;
    }

    return true;
  };

  const applyDefaultIncoterms = (incoterm: string | undefined, incotermDefault: string | undefined) => {
    if (incoterm === "off") return;

    const currentValue = getValues("service.incoterms");
    const nextValue = resolveDefaultIncoterms(incotermDefault, currentValue);
    if (nextValue && nextValue !== currentValue) {
      setValue("service.incoterms", nextValue);
    }
  };

  const clearSpecialRequirements = () => {
    const existing = getValues("service.special_requirements") || {};
    Object.keys(existing).forEach((key) => {
      unregister(`service.special_requirements.${key}.value`);
      unregister(`service.special_requirements.${key}.additional_information`);
    });
    setValue("service.special_requirements", {});
  };

  const setDefaultSpecialRequirements = (
    specialRequirementValues: Array<{ label: string; value: string }> = [],
  ) => {
    const existing = getValues("service.special_requirements") || {};
    const allowedKeys = new Set(specialRequirementValues.map((item) => item.value));

    Object.keys(existing).forEach((key) => {
      if (!allowedKeys.has(key)) {
        unregister(`service.special_requirements.${key}.value`);
        unregister(`service.special_requirements.${key}.additional_information`);
      }
    });

    const specialRequirements: Record<
      string,
      { label: string; value: string; additional_information: string }
    > = {};

    specialRequirementValues.forEach((item) => {
      const existingEntry = existing[item.value];
      const labelUnchanged = existingEntry?.label === item.label;

      if (labelUnchanged) {
        const isYes = existingEntry?.value === true || existingEntry?.value === "true";

        specialRequirements[item.value] = {
          label: item.label,
          value: isYes ? "true" : "false",
          additional_information: isYes ? existingEntry?.additional_information ?? "" : "",
        };
        return;
      }

      specialRequirements[item.value] = {
        label: item.label,
        value: "false",
        additional_information: "",
      };
    });

    setValue("service.special_requirements", specialRequirements);
  };

  const renderServiceSection = () => (
    <>
      {!isFetching && !getValues("organizational.location") && (
        <Row>
          <Col>
            <Alert variant="warning" className="mb-3 ml-3">
              <FormattedMessage
                id="new-request.organisational-details.selectPaymentLocationPrompt"
                defaultMessage="Please select a {paymentLocation} before continuing"
                values={{ paymentLocation: getPaymentLocationLabel() }}
              />
            </Alert>
          </Col>
        </Row>
      )}
      {isFetching && (
        <Card.Body>
          <Row>
            <Col className="pl-4">
              <div className="skeleton-card">
                <div>
                  <Row className="skeleton-row">
                    <span key={1} className="skeleton" />
                    <span key={2} className="skeleton" />
                    <span key={3} className="skeleton" />
                    <span key={4} className="skeleton" />
                  </Row>
                </div>
              </div>
            </Col>
          </Row>
        </Card.Body>
      )}

      {!!Object.keys(state.service)?.length && !isFetching && getValues("organizational.location") && (
        <>
          <div className="mb-24 flex-row">
            <div className="w-200">
              <FormItemInfo
                labelId="new-request.organisational-details.serviceLevel"
                label="Service level"
                descId="new-request.organisational-details.serviceLevelInfo"
                description="Provide info about service"
              />
            </div>
            <div>
              <div className="d-flex service-level-inputs">
                {state.service.incoterm !== "off" && (
                  <div className="w-192">
                    <SearchSelect
                      options={Object.values(state.service.incoterm_values)}
                      defaultValue={state.service.incoterm_default}
                      name="service.incoterms"
                      testId={testIds.REQUEST_FORM_SERVICE_INCOTERMS}
                      placeholder="Enter Incoterms"
                      required={state.service?.incoterm === "mandatory"}
                      label={intl.formatMessage({ id: "new-request.service.incoterms", defaultMessage: "Incoterms" })}
                      disable={state.disabledFields["service.incoterms"]}
                    />
                  </div>
                )}
                {state.service.request_service_type && (
                  <div className="w-192 ml-8">
                    <SearchSelect
                      options={state.service.service_type_options}
                      name="service.service_type"
                      testId={testIds.REQUEST_FORM_SERVICE_REQUIREMENT}
                      defaultValue="dtd"
                      required
                      label={intl.formatMessage({
                        id: "new-request.service.serviceRequirement",
                        defaultMessage: "Service requirement",
                      })}
                      disable={state.disabledFields["service.service_type"]}
                    />
                  </div>
                )}
              </div>
              {state.service.incoterm !== "off" && (
                <div className="mt-2 service-level-inputs">
                  <div className="w-392 mt-2">
                    <label>
                      <FormattedMessage
                        id="new-request.service.incotermInstructions"
                        defaultMessage="Incoterm instructions"
                      />
                    </label>
                    <Textarea
                      name="service.incoterm_instructions"
                      placeholder={intl.formatMessage({
                        id: "new-request.service.incotermInstructionsPlaceholder",
                        defaultMessage: "Enter Incoterm instructions",
                      })}
                      rows={2}
                      className="text-body bg-white"
                      validations={{ shouldUnregister: true }}
                    />
                  </div>
                </div>
              )}
              {state.service.ask_insurance !== "off" && (
                <>
                  <div className="mt-4 service-level-inputs">
                    <InputGroup className="checkbox bg-transparent mb-1">
                      <label htmlFor="insurance-option" className="d-flex align-items-center cursor-pointer mb-0 w-100">
                        <input
                          id="insurance-option"
                          type="checkbox"
                          className="d-none"
                          data-testid={testIds.REQUEST_FORM_SERVICE_INSURANCE_CHECKBOX}
                          {...register("service.insurance")}
                        />
                        <span className="border-dark border-solid border-1 mr-2 position-relative br-3 checkbox__box" />
                        <span className="text-primary mr-auto">
                          <FormattedMessage id="new-request.service.includeInsurance" defaultMessage="Include insurance" />
                        </span>
                      </label>
                    </InputGroup>
                  </div>
                  {insuranceEnabled && (
                    <div className="mt-2 service-level-inputs">
                      <div className="w-392 mt-2">
                        <Textarea
                          name="service.insurance_instructions"
                          placeholder="Insurance instructions"
                          rows={2}
                          className="text-body bg-white"
                          validations={{ shouldUnregister: true }}
                        />
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
          {showClearance && (
            <>
              <FormHr />
              <div className="mt-24 mb-24 flex-row">
                <div className="w-200">
                  <FormItemInfo
                    labelId="new-request.organisational-details.clearance"
                    label="Clearance"
                    descId="new-request.organisational-details.clearanceInfo"
                    description="Provide info about clearance"
                  />
                </div>
                {(isQuoteRequest ||
                  (Boolean(resolvedPickupCountry) &&
                    Boolean(resolvedDeliveryCountry) &&
                    resolvedPickupCountry !== resolvedDeliveryCountry)) && (
                  <div className="w-192 mr-8 d-flex flex-column justify-content-between">
                    <SearchSelect
                      options={EXPORT_CLEARANCE_OPTIONS(intl)}
                      defaultValue={state.service?.export_clearance_default || EXPORT_CLEARANCE_OPTIONS(intl)?.[0]?.value}
                      name="service.export_clearance"
                      testId={testIds.REQUEST_FORM_SERVICE_EXPORT_CLEARANCE}
                      placeholder="Select export clearance"
                      required
                      filterBy={filterWhenNonSelected}
                      disable={state.disabledFields["service.export_clearance"]}
                      label={
                        <FormattedMessage
                          id="new-request.service.exportClearanceNeeded"
                          defaultMessage="Export clearance needed?"
                        />
                      }
                    />
                    {exportClearance && exportClearance !== "no" && (
                      <Textarea
                        name="service.export_clearance_instructions"
                        placeholder={intl.formatMessage({
                          id: "new-request.service.exportClearanceInstructionsPlaceholder",
                          defaultMessage: "Instructions for export clearance (e.g. Broker details, customs number, etc.)",
                        })}
                        rows={2}
                        className="text-body bg-white mt-2 font-b3"
                      />
                    )}
                  </div>
                )}
                <div className="w-192 d-flex flex-column justify-content-between">
                  <SearchSelect
                    options={TRANSIT_CLEARANCE_OPTIONS(intl)}
                    defaultValue={state.service?.transit_clearance_default || TRANSIT_CLEARANCE_OPTIONS(intl)?.[0]?.value}
                    name="service.transit_clearance"
                    testId={testIds.REQUEST_FORM_SERVICE_TRANSIT_CLEARANCE}
                    placeholder="Select transit clearance"
                    required
                    disable={state.disabledFields["service.transit_clearance"]}
                    filterBy={filterWhenNonSelected}
                    label={
                      <FormattedMessage
                        id="new-request.service.transitClearanceNeeded"
                        defaultMessage="Transit clearance needed?"
                      />
                    }
                  />
                  {transitClearance && transitClearance !== "no" && (
                    <Textarea
                      name="service.transit_clearance_instructions"
                      placeholder={intl.formatMessage({
                        id: "new-request.service.transitClearanceInstructionsPlaceholder",
                        defaultMessage: "Instructions for transit clearance (e.g. Broker details, customs number, etc.)",
                      })}
                      rows={2}
                      className="text-body bg-white mt-2 font-b3"
                    />
                  )}
                </div>
                <div className="w-192 ml-8 d-flex flex-column justify-content-between">
                  <SearchSelect
                    options={IMPORT_CLEARANCE_OPTIONS(intl)}
                    defaultValue={state.service?.import_clearance_default || IMPORT_CLEARANCE_OPTIONS(intl)?.[0]?.value}
                    name="service.import_clearance"
                    testId={testIds.REQUEST_FORM_SERVICE_IMPORT_CLEARANCE}
                    placeholder="Select import clearance"
                    disable={state.disabledFields["service.import_clearance"]}
                    required
                    label={
                      <FormattedMessage
                        id="new-request.service.importClearanceNeeded"
                        defaultMessage="Import clearance needed?"
                      />
                    }
                  />
                  {importClearance && importClearance !== "no" && (
                    <Textarea
                      name="service.import_clearance_instructions"
                      placeholder={intl.formatMessage({
                        id: "new-request.service.importClearanceInstructionsPlaceholder",
                        defaultMessage: "Instructions for import clearance (e.g. Broker details, customs number, etc.)",
                      })}
                      rows={2}
                      className="text-body bg-white mt-2 font-b3"
                    />
                  )}
                </div>
              </div>
            </>
          )}
          {state.service?.request_service_requirement && (
            <>
              <FormHr />
              <ServiceRequirementForm state={state} />
            </>
          )}
          {!isQuoteRequest && (
            <>
              <FormHr />
              <ProviderAttachmentForm
                setBucketAttachments={setBucketAttachments}
                handleDraftSave={handleDraftSave}
                isQuickRequest
              />
            </>
          )}
        </>
      )}
    </>
  );

  if (isConsolidated)
    return (
      <FormSection>
        <FormHeader title="Service" />
        <div className="form-section-content">{renderServiceSection()}</div>
        {/* <div className="form-section-content">test</div> */}
      </FormSection>
    );

  return (
    <Card className="mb-3 form-container" data-testid={testIds.REQUEST_FORM_SERVICE_SECTION}>
      <Accordion.Toggle as={Card.Header} className="accordion-header" eventKey={FormStepTypes.SERVICE}>
        <div className="d-flex align-items-center">
          {(hasError || state.draftSaved[FormStepTypes.SERVICE]) && (
            <div className={`valid-status ${hasError ? "error" : ""}`}>{hasError ? <ExclamationIcon /> : <CheckIcon />}</div>
          )}
          <h5>
            <FormattedMessage id="new-request.index.service" defaultMessage="Service" />
          </h5>
        </div>
        <SortIcon
          fill="#000"
          width={8}
          height={7}
          transform={`rotate(${currentActiveStep === FormStepTypes.SERVICE ? 180 : 0})`}
        />
      </Accordion.Toggle>
      <Accordion.Collapse eventKey={FormStepTypes.SERVICE}>
        <Card.Body>{renderServiceSection()}</Card.Body>
      </Accordion.Collapse>
    </Card>
  );
}

export default ServiceForm;

ServiceForm.defaultProps = {
  validateSection: undefined,
  shouldValidateSection: undefined,
  isConsolidated: false,
  handleDraftSave: undefined,
  childRef: undefined,
};
